import { useTranslation } from 'react-i18next'
import { Check, Bell, Eye, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Appointment } from '@/lib/api'

export interface BottomSheetAction {
  key: string
  label: string
  icon?: React.ReactNode
  variant?: 'default' | 'outline' | 'ghost' | 'destructive'
  onClick: () => void
}

export interface BottomSheetProps {
  open: boolean
  appointment: Appointment | null
  onClose: () => void
  onView: () => void
  onReschedule?: () => void
  onComplete?: () => void
  onRemind?: () => void
}

export function BottomSheet({
  open,
  appointment,
  onClose,
  onView,
  onReschedule,
  onComplete,
  onRemind,
}: BottomSheetProps) {
  const { t } = useTranslation()

  const actions: BottomSheetAction[] = [
    { key: 'view', label: t('schedule.actions.view'), icon: <Eye className="h-4 w-4" />, onClick: onView },
  ]
  if (onReschedule) {
    // OPT-013：移动端"更改时间"入口，与拖拽同走 /move 直接生效
    actions.push({ key: 'reschedule', label: t('schedule.actions.moveTime'), icon: <CalendarClock className="h-4 w-4" />, onClick: onReschedule })
  }
  if (onComplete) {
    actions.push({ key: 'complete', label: t('schedule.actions.complete'), icon: <Check className="h-4 w-4" />, onClick: onComplete })
  }
  // OPT-012：取消预约入口已移除
  if (onRemind) {
    actions.push({ key: 'remind', label: t('schedule.actions.remind'), icon: <Bell className="h-4 w-4" />, onClick: onRemind })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="fixed bottom-0 left-0 right-0 top-auto translate-y-0 sm:translate-x-0 rounded-b-none sm:rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-left">
            {appointment?.title || t('schedule.untitled')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {actions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant || 'outline'}
              className="w-full justify-start gap-2"
              onClick={() => {
                action.onClick()
                onClose()
              }}
            >
              {action.icon}
              {action.label}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
